import { useParams } from "react-router-dom";
import { FaPlay, FaPause } from "react-icons/fa";
import { useCallback, useEffect, useState } from "react";
import PlayerBackground from "../../components/PlayerBackground";
import FadeInAnimation from "../../components/FadeInAnimation";
import ScrollText from "../../components/ScrollText";
import "./PlayerCompact.css";

// Importar funciones centralizadas
import {
  isNewSong,
  getCover,
  createAudioElement,
  preloadStationBackground,
  updateMediaSession,
  toUpperCase,
} from "../../services/audioServices";

function PlayerCompact() {
  const { port, name } = useParams();

  // Precargar inmediatamente la imagen de fondo
  useEffect(() => {
    if (name) {
      // Precargar la imagen de fondo lo antes posible
      preloadStationBackground(name);
    }
  }, [name]);

  // Función para capitalizar la primera letra
  const capitalize = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Aplicamos capitalización al nombre de la estación
  const displayName = capitalize(name);

  // URLs de imágenes
  const logoUrl = `https://audio.streaminghd.cl:2020/pub/${name}/background.jpg`;
  const defaultLogoUrl = `https://vod.streaminghd.cl/img/default.jpg`;

  // Estado para el reproductor
  const [audio, setAudio] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [logo, setLogo] = useState(logoUrl);
  const [currentLogo, setCurrentLogo] = useState(logoUrl);
  const [fadeOut, setFadeOut] = useState(false);
  const [song, setSong] = useState("");
  const [singer, setSinger] = useState("");
  const [originalSong, setOriginalSong] = useState("");
  const [originalSinger, setOriginalSinger] = useState("");

  // Handlers de eventos de audio
  const handleAudioPause = () => setPlaying(false);
  const handleAudioPlay = () => setPlaying(true);
  const handleAudioEnded = () => setPlaying(false);

  // Función para manejar errores de imagen
  const handleImageError = ({ currentTarget }) => {
    currentTarget.onerror = null;
    // Si falla, usar la imagen por defecto
    currentTarget.src = defaultLogoUrl;
  };

  // Efecto para manejar el cambio de logo con fade
  useEffect(() => {
    if (logo !== currentLogo) {
      // Iniciar fade out
      setFadeOut(true);

      // Después de un breve retraso, actualizar la imagen y hacer fade in
      const timer = setTimeout(() => {
        setCurrentLogo(logo);
        setFadeOut(false);
      }, 300); // 300ms para el fade out

      return () => clearTimeout(timer);
    }
  }, [logo, currentLogo]);

  // Inicializar el audio
  useEffect(() => {
    if (port && name) {
      // Usar la función centralizada para crear el elemento de audio
      const audioElement = createAudioElement(
        port,
        name,
        setAudio,
        setAudioError
      );
      setAudio(audioElement);

      // Limpiar al desmontar
      return () => {
        if (audioElement) {
          audioElement.pause();
          audioElement.src = "";
        }
      };
    }
  }, [port, name]);

  // Función para obtener la canción actual
  const getCurrentSong = useCallback(() => {
    isNewSong(port, singer, song).then(
      (response) => {
        // Usar el nuevo formato de entrada para getCover como objeto
        getCover({
          singer: response.singer,
          song: response.song,
        }).then((cover) => {
          // Guardar versiones temporales para actualizar después
          const newSong = response.song;
          const newSinger = response.singer;
          const newOriginalSong = response.originalSong || response.song;
          const newOriginalSinger = response.originalSinger || response.singer;

          // Retraso de 2 segundos antes de cambiar la información
          setTimeout(() => {
            // Guardar versiones limpias para búsqueda futura
            setSong(newSong);
            setSinger(newSinger);

            // Guardar versiones originales con paréntesis
            setOriginalSong(newOriginalSong);
            setOriginalSinger(newOriginalSinger);

            // Actualizar el cover
            setLogo(cover);

            // Actualizar MediaSession para iOS
            updateMediaSession(
              newOriginalSong, // título
              newOriginalSinger, // artista
              displayName, // álbum (nombre de la estación)
              cover // portada
            );
          }, 2000);

          setTimeout(getCurrentSong, 10000);
        });
      },
      () => {
        setTimeout(getCurrentSong, 10000);
      }
    );
  }, [port, singer, song, displayName]);

  // Control de reproducción
  const onTogglePlay = () => {
    if (playing) {
      // Si está reproduciendo, pausar y eliminar
      if (audio) {
        audio.pause();
        audio.src = "";
        setAudio(null);
      }
      setPlaying(false);

      // Actualizar estado de MediaSession
      if ("mediaSession" in navigator) {
        navigator.mediaSession.playbackState = "paused";
      }
    } else {
      // Si está pausado, crear nueva conexión
      setPlaying(true);

      const streamUrl = `https://audio.streaminghd.cl:2000/stream/${name}/;stream.mp3`;
      const newAudio = new Audio(streamUrl);

      newAudio.play().catch(() => {
        // Si falla, intentar URL alternativa
        const alternativeUrl = `http://audio.streaminghd.cl:${port}/;stream.mp3`;
        const alternativeAudio = new Audio(alternativeUrl);

        alternativeAudio.play().catch(() => {
          setPlaying(false);
          setAudioError(true);
        });

        setAudio(alternativeAudio);
      });

      setAudio(newAudio);

      // Actualizar estado de MediaSession y metadatos
      if ("mediaSession" in navigator) {
        navigator.mediaSession.playbackState = "playing";

        // Actualizar metadatos con la información actual
        updateMediaSession(originalSong, originalSinger, displayName, logo);
      }
    }
  };

  // Efecto para manejar eventos de audio
  useEffect(() => {
    if (!audio) return;

    audio.addEventListener("pause", handleAudioPause);
    audio.addEventListener("play", handleAudioPlay);
    audio.addEventListener("ended", handleAudioEnded);

    return () => {
      audio.removeEventListener("pause", handleAudioPause);
      audio.removeEventListener("play", handleAudioPlay);
      audio.removeEventListener("ended", handleAudioEnded);
    };
  }, [audio]);

  // Efecto para iniciar la obtención de datos
  useEffect(() => {
    getCurrentSong();
  }, [getCurrentSong]);

  // Efecto para actualizar el título de la página cuando cambia la canción
  useEffect(() => {
    if (originalSong && originalSinger) {
      // Actualizar el título de la página con la canción y el artista
      document.title = `${originalSong} - ${originalSinger} | ${displayName}`;
    } else if (originalSong) {
      // Si solo tenemos la canción
      document.title = `${originalSong} | ${displayName}`;
    } else {
      // Si no hay información de la canción, mostrar solo el nombre de la estación
      document.title = `(Live) ${toUpperCase(displayName)}`;
    }
  }, [originalSong, originalSinger, displayName]);

  // Renderizado del componente
  return (
    <div className="PlayerCompact">
      {/* Reemplazando el fondo con el componente centralizado */}
      <PlayerBackground
        backgroundImage={logo ? logo : logoUrl}
        fallbackImage={defaultLogoUrl}
        type="compact"
        fadeOut={fadeOut}
      />

      {/* Contenido del reproductor */}
      <div className="CompactContent">
        {/* Imagen (izquierda) */}
        <FadeInAnimation>
          <div className="CompactCover">
            <img
              src={logo ? logo : logoUrl}
              alt={singer ? `${singer} - ${song}` : toUpperCase(displayName)}
              className="CompactCoverImage"
              onError={handleImageError}
            />
          </div>
        </FadeInAnimation>

        <FadeInAnimation>
          <div className="CompactRightSection">
            {/* Animación "Al Aire" (arriba del título) */}
            <div className="CompactOnAir">
              <div className="OnAirText">
                <span>AL</span>
                <span className="OnAirHighlight">AIRE</span>
              </div>
              <div className="OnAirDot"></div>
            </div>

            {/* Información (centro) */}
            <div className="CompactInfo">
              <ScrollText
                text={originalSong || toUpperCase(displayName)}
                className="CompactSongTitle"
                elementType="div"
              />
              <ScrollText
                text={originalSinger || " "}
                className="CompactArtistName"
                elementType="div"
              />
            </div>
          </div>
        </FadeInAnimation>

        {/* Control (derecha) */}
        <FadeInAnimation>
          <div className="CompactControls">
            <button
              className="CompactPlayButton"
              onClick={onTogglePlay}
              aria-label={playing ? "Pausar" : "Reproducir"}
              disabled={audioError}
            >
              {playing ? <FaPause /> : <FaPlay />}
            </button>
          </div>
        </FadeInAnimation>
      </div>
    </div>
  );
}

export default PlayerCompact;
